import LinkIcon from '@/app/assets/svg/link.svg?react';
import {
  HistoryTab,
  usePortfolioHistory,
} from '@/app/components/PortfolioPageComponents/hooks/usePortfolioHistory';
import { AppLink } from '@/app/shared/ui/AppLink';
import DataTable, {
  DataTableColumn,
  DataTableStacked,
  DataTableValue,
} from '@/app/shared/ui/DataTable.tsx';
import { Skeleton } from '@/app/shared/ui/Skeleton';

interface PortfolioHistoryTableProps {
  activeTab: HistoryTab;
}

const desktopGridClassName =
  'min-[960px]:grid min-[960px]:w-full min-[960px]:min-w-0 min-[960px]:grid-cols-[minmax(0,1.3fr)_minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,0.45fr)_minmax(0,0.45fr)_minmax(0,1fr)_32px] min-[960px]:items-center min-[960px]:gap-2 min-[960px]:px-5 xl:gap-4';

type HistoryRow = {
  id: string;
  action: string;
  vault: string;
  amount: string;
  amountUsd: string;
  share: string;
  fee: string;
  date: string;
  txHash: string;
};

const HISTORY_COLUMNS: DataTableColumn<HistoryRow>[] = [
  {
    id: 'action',
    header: 'Action',
    cell: (row) => <DataTableValue value={row.action} />,
  },
  {
    id: 'vault',
    header: 'Vault / Tranche',
    cell: (row) => <DataTableValue value={row.vault} />,
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: (row) => (
      <DataTableStacked primary={row.amount} secondary={row.amountUsd} secondaryClassName='text-sm text-gray-500' />
    ),
  },
  {
    id: 'share',
    header: 'Share',
    cell: (row) => <DataTableValue value={row.share} />,
  },
  {
    id: 'fee',
    header: 'Fee',
    cell: (row) => <DataTableValue value={row.fee} />,
  },
  {
    id: 'date',
    header: 'Date',
    cell: (row) => <DataTableValue value={row.date} />,
  },
];

const PortfolioHistoryTable = ({ activeTab }: PortfolioHistoryTableProps) => {
  const { rows, isLoading, isConnected, getTxUrl } = usePortfolioHistory(activeTab);

  if (isLoading) {
    return (
      <div className='flex flex-col gap-3'>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton.Line key={index} loading className='h-[74px] w-full rounded-md'>
            <div className='h-[74px]' />
          </Skeleton.Line>
        ))}
      </div>
    );
  }

  if (!isConnected) {
    return <p className='text-base text-gray-400'>Connect wallet to see transaction history</p>;
  }

  return (
    <DataTable
      data={rows}
      getRowKey={(row) => row.id}
      desktopGridClassName={desktopGridClassName}
      emptyText='No transactions yet'
      pagination={{ desktop: 5, mobile: 2 }}
      listClassName='min-w-0 gap-2'
      desktopRowClassName='h-[74px] py-4'
      mobileRowClassName='p-4'
      headerTrailing={<span />}
      columns={HISTORY_COLUMNS}
      rowTrailing={(row) => (
        <AppLink
          to={getTxUrl(row.txHash)}
          external
          className='justify-self-end cursor-pointer text-brand-1100'
        >
          <LinkIcon className='h-4.5 w-4.5' />
        </AppLink>
      )}
      renderMobile={(row) => (
        <div className='flex flex-col gap-4'>
          <div className='grid grid-cols-2 gap-x-4 gap-y-4'>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Action</span>
              <DataTableValue value={row.action} />
            </div>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Vault / Tranche</span>
              <DataTableValue value={row.vault} />
            </div>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Amount</span>
              <DataTableStacked
                primary={row.amount}
                secondary={row.amountUsd}
                secondaryClassName='text-sm text-gray-500'
              />
            </div>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Share</span>
              <DataTableValue value={row.share} />
            </div>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Fee</span>
              <DataTableValue value={row.fee} />
            </div>
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-500'>Date</span>
              <DataTableValue value={row.date} />
            </div>
          </div>
          <AppLink
            to={getTxUrl(row.txHash)}
            external
            className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-brand-1100 py-3 text-base text-brand-1100'
          >
            View Details
            <LinkIcon className='h-4 w-4' />
          </AppLink>
        </div>
      )}
    />
  );
};

export default PortfolioHistoryTable;
